<template>
    <section v-show="getOperatorRealtime" class="realtime-section">
        <div id="queue-table-container">
            <b-button 
                v-b-toggle.queue-table-component 
                class="queue-info-toggle" 
                @click="queueInfoShowing = !queueInfoShowing"
            >
                <i class="fas fa-arrow-up" :class="{'showing': queueInfoShowing, 'hidden': !queueInfoShowing}"></i>
                {{queueInfoShowing ? 'Ocultar informações de fila' : 'Exibir informações de fila'}}
                <div v-if="queueCallsCount > 0" class="badge bg-danger">{{ queueCallsCount }}</div>
            </b-button>
            <b-collapse id="queue-table-component">
                <OrQueueTable 
                    :queueCalls="queueCalls" 
                    :queueList="queueList"
                    :socketClient="socketClient"
                    :reconnection="reconnection"
                    :enabledListeners="enabledListeners"
                    @callsCount="updateCallsCount"
                />
            </b-collapse>
        </div>
        <div id="users-table-container">
            <OrUsersTable 
                :usersList="usersList"
                :queueList="queueList"
                :currentCalls="currentCalls"
                :peersStatus="peersStatus"
                :queueMembers="queueMembers"
                :socketClient="socketClient"
                :reconnection="reconnection"
                :enabledListeners="enabledListeners"
            />
        </div>
    </section>
</template>

<script>
    import OrUsersTable from './ORUsersTable';
    import OrQueueTable from './ORQueueTable'
    import { mapGetters } from "vuex";
    export default {
        components: {
            OrUsersTable,
            OrQueueTable
        },
        data() {
            return {
                //Operator Realtime - Credenciais
                orUrl: process.env.MIX_APP_URL || 'https://auster-pabx.advancetelecom.com.br',
                orPort: process.env.MIX_OR_PORT || '3009',
                orUser: process.env.MIX_OR_USERNAME || 'Livio',
                orPassword: process.env.MIX_OR_PASSWORD || 'operator-realtime-demo',
                orToken: null,
                renewed: false,

                // Operator Realtime - Variaveis de response das rotas
                currentCalls: [],
                queueMembers: [],
                peersStatus: [],
                queueCalls: [],

                queueList: [],
                usersList: [],
                socketClient: null,
                connectionCheck: false,
                reconnection: false,

                enabledListeners: {
                    queueCalls: false,
                    currentCalls: false,
                    queueMembers: false,
                    peerStatus: false,
                },

                queueInfoShowing: false,
                queueCallsCount: 0
            }
        },
        computed: {
            ...mapGetters(["getStoreQueues", "getOperatorRealtime"]),
        },
        async created(){
            await this.setupSocketConnection()
        },
        async mounted(){
            const token = localStorage.getItem('orToken')
            if(!token){
                await this.getAPIToken()
            } else {
                this.orToken = token
            }
            
            await this.getUsersList()

            await this.getQueueCalls()
            await this.getCurrentCalls()
            await this.getQueueMembers()
            await this.getPeersStatus()
        },
        methods: {
            async getUsersList(){
                await axios.get('/list-queues-users').then(({data}) => {
                    if(data && data.length){
                        this.usersList = data
                    }
                })
            },

            async getAPIToken(){
                this.renewed = false
                return await fetch(`${this.orUrl}:${this.orPort}/generate-token`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        username: this.orUser,
                        password: this.orPassword,
                    }),
                    credentials: 'include',
                })
                .then(response => response.json())
                .then(({token}) => {
                    if(token){
                        this.orToken = token
                        localStorage.setItem('orToken', token);
                        this.renewed = true
                    }
                })
                .catch(error => console.error('Erro:', error));
            },

            async getQueueCalls(){
                this.enabledListeners = { ...this.enabledListeners, queueCalls: false };
                fetch(`${this.orUrl}:${this.orPort}/queue-entry`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.orToken}`
                    },
                    credentials: 'include',
                })
                .then(response => response.json())
                .then(async data => {
                    if(data.error){
                        if(data.error.includes('expirado') || data.error.includes('ausente')){
                            if(!this.renewed){
                                await this.getAPIToken().then(async () => {
                                    await this.getQueueCalls()
                                })
                            }
                        }
                    } else {
                        this.queueCalls = data
                        this.enabledListeners = { ...this.enabledListeners, queueCalls: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
            },

            async getCurrentCalls(){
                this.enabledListeners = { ...this.enabledListeners, currentCalls: false };
                fetch(`${this.orUrl}:${this.orPort}/core-show-channels`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.orToken}`
                    },
                    credentials: 'include',
                })
                .then(response => response.json())
                .then(async data => {
                    if(data.error){
                        if(data.error.includes('expirado') || data.error.includes('ausente')){
                            if(!this.renewed){
                                await this.getAPIToken().then(async () => {
                                    await this.getCurrentCalls()
                                })
                            }
                        }
                    } else {
                        this.currentCalls = data
                        this.enabledListeners = { ...this.enabledListeners, currentCalls: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
            },

            async getQueueMembers(){
                this.enabledListeners = { ...this.enabledListeners, queueMembers: false };
                fetch(`${this.orUrl}:${this.orPort}/queue-member`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.orToken}`
                    },
                    credentials: 'include',
                })
                .then(response => response.json())
                .then(async data => {
                    if(data.error){
                        if(data.error.includes('expirado') || data.error.includes('ausente')){
                            if(!this.renewed){
                                await this.getAPIToken().then(async () => {
                                    await this.getQueueMembers()
                                })
                            }
                        }
                    } else {
                        this.queueMembers = data
                        this.enabledListeners = { ...this.enabledListeners, queueMembers: true };                    
                    }
                })
                .catch(error => console.error('Erro:', error));
            },

            async getPeersStatus(){
                this.enabledListeners = { ...this.enabledListeners, peerStatus: false };
                fetch(`${this.orUrl}:${this.orPort}/sip-peers`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.orToken}`
                    },
                    credentials: 'include',
                })
                .then(response => response.json())
                .then(async data => {
                    if(data.error){
                        if(data.error.includes('expirado') || data.error.includes('ausente')){
                            if(!this.renewed){
                                await this.getAPIToken().then(async () => {
                                    await this.getPeersStatus()
                                })
                            }
                        }
                    } else {
                        this.peersStatus = data
                        this.enabledListeners = { ...this.enabledListeners, peerStatus: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
            },

            async setupSocketConnection(){
                if (this.socketClient && this.socketClient.connected) {
                    this.socketClient.removeAllListeners();
                    this.socketClient.disconnect();
                }
                this.socketClient = io.connect(`${location.protocol}//${location.hostname}:3009`, {
                    reconnection: true,
                    reconnectionDelay: 2000,
                    reconnectionDelayMax: 10000,
                    randomizationFactor: 0.5,
                    pingTimeout: 600000,
                    pingInterval: 120000,
                    query: {
                        userId: user.getId(),
                        userName: user.getName()
                    },
                });

                if(this.socketClient){
                    this.socketClient.on('connect', async () => {
                        this.reconnection = false
                        if(this.connectionCheck){
                            console.info('[Operator Realtime] Reconexão ao server');
                            await this.getUsersList()
                            await this.getQueueCalls()
                            await this.getCurrentCalls()
                            await this.getQueueMembers()
                            await this.getPeersStatus()
                        } else {
                            console.info('[Operator Realtime] Nova conexão ao server');
                            this.connectionCheck = true
                        }
                    });
                
                    this.socketClient.on('disconnect', (reason) => {
                        console.info(`Desconectado: ${reason}`);
                        this.reconnection = true
                        this.usersList = []
                        this.currentCalls = []
                        this.queueMembers = []
                        this.peersStatus = []
                        this.queueCalls = []
                        this.enabledListeners = {
                            queueCalls: false,
                            currentCalls: false,
                            queueMembers: false,
                            peerStatus: false,
                        }
                    });
                
                    this.socketClient.on('reconnect_attempt', (attemptNumber) => {
                        console.info(`Tentativa de reconexão #${attemptNumber}`);
                    });
                } 
            },
            updateCallsCount(count){
                this.queueCallsCount = count
            }
        },
        watch: {
            getStoreQueues(val){
              this.queueList = val
            }
        }
    }

</script>

<style lang="scss" scoped>
    .realtime-section {
        max-height: 88vh;
        overflow-y: auto;
    }

    #users-table-container {
        margin: 20px;
    }

    #queue-table-container {
        display: flex;
        justify-content: center;
        flex-direction: column;
        margin: 10px;
    }

    #queue-table-component {
        margin: 5px;
    }

    .fa-arrow-up {
        transition: 0.6s ease-in-out;
    }

    .showing {
        transform: rotate(0deg);
    }

    .hidden {
        transform: rotate(180deg);
    }

    .queue-info-toggle {
        background-color: #1c869a;
        color: white;
        border: none;
        padding: 0 10px;
        transition: 0.2s;
        font-size: unset;
        border-radius: 5px !important;
        width: 280px !important;
        align-self: center;
        display: flex;
        align-items: center;
        gap: 10px;
        justify-content: center;

        .badge {
            top: unset;
            border-radius: 10px;
            padding: 3px 6px;
        }

        &:hover {
            background-color: #176b7c;
            color: white;
        }

        &:focus {
            outline: none;
            box-shadow: none;
        }
    }
</style>